import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const DEFAULT_REOPEN_REASON =
  "The IRS could not accept this name, usually because a very similar name is already registered. This is rare and easy to fix. Choose an adjusted or new name and we will take it from there.";

type Props = {
  memberRecordId: string;
  memberName?: string;
  onDone?: () => void;
  size?: "sm" | "default";
  className?: string;
};

export default function ReopenNameDialog({
  memberRecordId,
  memberName,
  onDone,
  size = "sm",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(DEFAULT_REOPEN_REASON);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const start = () => {
    setReason(DEFAULT_REOPEN_REASON);
    setConfirmText("");
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("reopen-una-name", {
      body: { member_record_id: memberRecordId, reason: reason.trim().slice(0, 500) },
    });
    setBusy(false);

    if (error || (data as any)?.error) {
      let message = "That did not go through. Please try again.";
      try {
        const parsed = await (error as any)?.context?.json?.();
        if (parsed?.error) message = parsed.error;
        else if ((data as any)?.error) message = (data as any).error;
      } catch (_e) {
        if ((data as any)?.error) message = (data as any).error;
      }
      toast.error(message);
      return;
    }

    toast.success("The name is open again and the member has been told.");
    setOpen(false);
    onDone?.();
  };

  return (
    <>
      <Button size={size} variant="outline" className={`text-destructive border-destructive/40 hover:bg-destructive/10 ${className ?? ""}`} onClick={start}>
        Reopen name
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen the Association name</DialogTitle>
            <DialogDescription>
              Use this when the IRS turned the name down, or for another rare correction.
              {memberName ? ` This is for ${memberName}.` : ""} The name goes back to the member,
              their confirmation is cleared, and the review runs again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reopen-reason">Reason for the member</Label>
              <Textarea
                id="reopen-reason"
                rows={5}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{reason.length} of 500 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reopen-confirm">Type REOPEN to continue</Label>
              <Input
                id="reopen-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="REOPEN"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={submit}
              disabled={busy || confirmText.trim() !== "REOPEN" || !reason.trim()}
            >
              Reopen the name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
